import {
  Content,
  CodeLink,
  ContentMetadata,
  ContentNode,
  ContentNodeType,
  ContentSubType,
  ContentType,
  ContentTypeMetadata,
} from '@amzn/awscat-pattern-library-service-model';
import * as UtilApi from '../../api/UtilsApi';
import { ContentTemplateMap } from '../../components/utils/SelectableOptions';
import { ContentSchemaSection } from '../../model/ContentSchemaSection';
import { supportsCodeRepos } from '../../api/ContentUtil';

export const convertContentTypeMetadata = (
  contentTypeMetadata: ContentTypeMetadata,
  contentSubType: ContentSubType,
): ContentTypeMetadata => {
  const { templateId, templateVersion } = UtilApi.getCurrentTemplateInfo(contentSubType);
  return Object.assign(contentTypeMetadata, {
    templateId,
    templateVersion,
    contentSubType,
  });
};

export const convertContentMetadata = (
  contentMetadata: ContentMetadata,
  contentType: ContentType,
): ContentMetadata => {
  return Object.assign(contentMetadata, {
    contentType,
  });
};

export const convertTemplateMetadata = (
  content: Content,
  contentSubType: ContentSubType,
): Content => {
  const templateSchema = getTemplateSchema(contentSubType, ContentSchemaSection.TemplateMetadata);
  const { templateMetadata } = content;
  const newTemplateMetadata = {};

  Object.entries(templateSchema.properties).forEach(([key, value]) => {
    if (key in templateMetadata) {
      // @ts-expect-error TS7053 - strict migration
      newTemplateMetadata[key] = templateMetadata[key];
    }
  });
  return Object.assign(content, {
    templateMetadata: newTemplateMetadata,
  });
};

export const getTemplateSchema = (
  contentSubType: ContentSubType,
  schemaSection: ContentSchemaSection,
) => {
  const { templateId, templateVersion } = UtilApi.getCurrentTemplateInfo(contentSubType);
  // @ts-expect-error TS2532 - strict migration
  const schema = ContentTemplateMap.get(templateId).get(templateVersion);
  return schema.definitions[schemaSection];
};

export const getH2Heading = (content: string) => {
  return `<br><Box variant='h2'> ${content} </Box>`;
};

export const getH3Heading = (content: string) => {
  return `<br><Box variant='h3'> ${content} </Box>`;
};

export const getContentPages = (content: Content) => {
  const {
    contentData: { id, title, contentData, subContent },
  } = content;
  const node = Object.assign(new ContentNode(), {
    id,
    title,
    nodeType: ContentNodeType.Page,
    contentData,
  });
  const contentPages = [node];
  subContent.forEach((page) => contentPages.push(page));
  return contentPages;
};

export const getContentSections = (content: Content) => {
  const {
    contentData: { contentSections },
  } = content;
  return contentSections;
};

export const getTemplateSections = (contentSubType: ContentSubType) => {
  const sectionSchema = getTemplateSchema(contentSubType, ContentSchemaSection.ContentSections);
  const sectionProps = sectionSchema.properties;
  const sections = Object.keys(sectionProps).map((id) => {
    const { label } = sectionProps[id];
    return Object.assign(new ContentNode(), {
      id,
      title: label,
    });
  });
  return sections;
};

export const getSeparator = () => {
  return `<hr id='metadata-divider' />`;
};

export const convertCodeRepository = (updatedContent: Content, toContentType: ContentType) => {
  const { contentData, contentMetadata, contentTypeMetadata } = updatedContent;
  const { codeLinks } = contentTypeMetadata;
  if (
    codeLinks &&
    codeLinks.length > 0 &&
    contentMetadata.contentType === toContentType &&
    !supportsCodeRepos(toContentType)
  ) {
    const linksContent = getLinksContent(codeLinks);
    const codeRepoSection = Object.assign(new ContentNode(), {
      id: 'codeRepository',
      title: 'Code repository',
      nodeType: ContentNodeType.Section,
      contentData: linksContent,
    });
    contentData.contentSections.push(codeRepoSection);
    // @ts-expect-error TS2790 - strict migration
    delete contentTypeMetadata.codeLinks;
  }
};

const getLinksContent = (codeLinks: CodeLink[]) => {
  let linksContent = '<TextContent><ul>';
  codeLinks.forEach(
    (link) =>
      (linksContent += `<li key='${link.url}'><a href='${link.url}' rel='noopener noreferrer' target='_blank'>${link.label}</a></li>`),
  );
  linksContent += '</ul></TextContent>';
  return linksContent;
};
